import type { MouseEvent } from 'react';
import { ChevronLeft, ChevronRight, Image as ImageIcon, X } from 'lucide-react';
import { Button } from '@ooh/ui/components/button';
import type {
  ClientOfferReviewLightboxLabels,
  ClientOfferReviewUnit,
} from './client-offer-review-types';

/**
 * Photo lightbox. Renders `unit.displayUrl` (display rendition) for every
 * simulated frame (there is only one real seeded photo per unit, not a
 * per-frame dataset) — falls back to the "no photo" placeholder when
 * `displayUrl` is `null`.
 */
export function ClientOfferReviewLightboxModal({
  unit,
  idx,
  onClose,
  onPrev,
  onNext,
  labels,
}: {
  unit: ClientOfferReviewUnit;
  idx: number;
  onClose: () => void;
  onPrev: () => void;
  onNext: () => void;
  labels: ClientOfferReviewLightboxLabels;
}) {
  const n = labels.frames.length;
  const frameIx = ((idx % n) + n) % n;
  const stop = (e: MouseEvent) => e.stopPropagation();
  // unit.dimensions is already fully formatted ("{width} x {height} m") by
  // the server — appending " m" here double-suffixed it.
  const caption = `${unit.address}, ${unit.city} · ${unit.format} ${unit.dimensions}`;

  return (
    // DS-BYPASS: always near-black overlay chrome, independent of the app's
    // light/dark theme (packages/ui/src/styles/globals.css has no always-dark
    // surface token). Same reasoning as the SSO bar in client-offer-review-header.tsx.
    <div
      onClick={onClose}
      data-testid="lightbox-modal"
      className="fixed inset-0 z-[80] flex flex-col items-center justify-center gap-3.5 bg-zinc-950/90"
    >
      <Button
        type="button"
        variant="ghost"
        size="icon-sm"
        onClick={onClose}
        aria-label={labels.close}
        className="absolute right-4 top-4 h-9 w-9 rounded-full bg-white/15 text-white hover:bg-white/25 hover:text-white"
      >
        <X className="h-4 w-4" strokeWidth={2} />
      </Button>
      <div
        onClick={stop}
        className="relative flex items-center justify-center"
        style={{ width: 'min(92vw, 1400px)', height: 'min(78vh, 900px)' }}
      >
        <div className="relative flex h-full w-full items-center justify-center overflow-hidden rounded-xl bg-gradient-to-br from-zinc-700 to-zinc-800 text-zinc-400">
          {unit.displayUrl == null ? (
            <div className="flex flex-col items-center gap-2.5">
              <ImageIcon width={52} height={52} strokeWidth={1.2} />
              <span className="text-sm">{labels.frames[frameIx]}</span>
            </div>
          ) : (
            <>
              <img src={unit.displayUrl} alt="" className="h-full w-full object-cover" />
              <span className="absolute bottom-3 left-1/2 -translate-x-1/2 rounded-md bg-black/55 px-2 py-0.5 text-xs text-white">
                {labels.frames[frameIx]}
              </span>
            </>
          )}
        </div>
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          onClick={(e) => {
            e.stopPropagation();
            onPrev();
          }}
          aria-label={labels.previous}
          data-testid="lightbox-prev"
          className="absolute -left-2 top-1/2 h-11 w-11 -translate-x-full -translate-y-1/2 rounded-full bg-white/15 text-white hover:bg-white/25 hover:text-white"
        >
          <ChevronLeft className="h-[18px] w-[18px]" strokeWidth={2.2} />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          onClick={(e) => {
            e.stopPropagation();
            onNext();
          }}
          aria-label={labels.next}
          data-testid="lightbox-next"
          className="absolute -right-2 top-1/2 h-11 w-11 translate-x-full -translate-y-1/2 rounded-full bg-white/15 text-white hover:bg-white/25 hover:text-white"
        >
          <ChevronRight className="h-[18px] w-[18px]" strokeWidth={2.2} />
        </Button>
      </div>
      <div onClick={stop} className="flex flex-col items-center gap-0.5">
        <span className="text-sm font-medium text-white">{caption}</span>
        <span className="text-xs tabular-nums text-zinc-400">
          {frameIx + 1} / {n}
        </span>
      </div>
    </div>
  );
}
